// F14 拡張: ③ キャスト画像の自動生成と差し替え（OpenRouter 直接・計画書 §16.10）。
// 生成画像は projects/<PID>/image-prompts/images/<castId>.<ext> に保存し、index.json のエントリへ
// imageFile / imageGeneratedAt / imageModel / imageSource を追記する（外部ツール画像の手動差し替えも同じ置き場）。
import { writeFileSync, mkdirSync, existsSync, unlinkSync } from 'node:fs';
import path from 'node:path';
import {
    callImage, callLlm, llmConfig, openRouterModelSettings, resolveImageCallSpec,
} from './llm-client.mjs';
import { toTransparentPng } from './image-postprocess.mjs';
import { batchCharacterConcurrency, fillTemplate } from './story-shared.mjs';
import { mapWithConcurrency } from './async-pool.mjs';
import { projectPaths, requireProjectDir, recordStep } from './story-project.mjs';
import { readJsonIfExists, writeJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';

export const IMAGE_EXT_BY_TYPE = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** 先頭バイトのマジックナンバーで画像形式を判定（png / jpeg / webp のみ対応）。 */
export function sniffImageType(bytes) {
    if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
    if (
        bytes.length >= 12 &&
        bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
        bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
    ) return 'image/webp';
    return null;
}

function readIndexOrThrow(p) {
    const idx = readJsonIfExists(p.imagePromptsIndex);
    if (!idx || !Array.isArray(idx.prompts) || idx.prompts.length === 0) {
        throw new Error('画像プロンプトが未生成です。先に「③-1 画像生成プロンプト」を実行してください。');
    }
    return idx;
}

/** 既存の同キャスト画像（拡張子違い）を消してから保存する（1 キャスト 1 枚の上書き運用）。 */
function writeCastImage(p, castId, bytes, ext) {
    mkdirSync(p.imagesDir, { recursive: true });
    for (const e of Object.values(IMAGE_EXT_BY_TYPE)) {
        const old = path.join(p.imagesDir, `${castId}.${e}`);
        if (e !== ext && existsSync(old)) unlinkSync(old);
    }
    const fileName = `${castId}.${ext}`;
    writeFileSync(path.join(p.imagesDir, fileName), bytes);
    return fileName;
}

function updateIndexEntry(p, idx, castId, patch) {
    idx.prompts = idx.prompts.map((e) => {
        if (e.castId !== castId) return e;
        const next = { ...e, ...patch };
        // 画像を差し替えたら「リグなし」切替（③でのリギング失敗フォールバック）の記録を解除する。
        // リギング可否は入力画像から作るモデル形状で決まるため、新しい画像ではリギングへ再挑戦する
        if (patch.imageGeneratedAt) {
            delete next.riggingUnsupported;
            delete next.riggingUnsupportedReason;
        }
        return next;
    });
    writeJson(p.imagePromptsIndex, idx);
}

/** 実効の画像生成プロバイダー/モデル（UI 表示・ログ用。実体は llm-client の resolveImageCallSpec）。 */
export function resolveImageModel(model = null, provider = null) {
    const spec = resolveImageCallSpec(llmConfig().image ?? {}, { provider, model });
    return spec.model;
}

export function resolveImageSpec(opts = {}) {
    return resolveImageCallSpec(llmConfig().image ?? {}, opts);
}

// ── アダルト系表現の検知と回避（2026-07-08・計画書§16.10） ─────────────
// 検証結果: 完全ヌードは grok 含む全モデルが拒否。露出高め衣装〜下着姿は grok のみ通過
// （klein は露出高め衣装の時点でリクエスト拒否）。UI は生成前に選択を出し、nsfwPolicy で挙動を切替える。

/** config/llm.json の image.nsfw（未設定でも安全に動くよう既定値を補う）。 */
export function nsfwConfig() {
    const n = llmConfig().image?.nsfw ?? {};
    return {
        recommendedModel: openRouterModelSettings().effective.imageAdult
            ?? (typeof n.recommendedModel === 'string' && n.recommendedModel.trim() !== '' ? n.recommendedModel : null),
        keywords: Array.isArray(n.keywords) ? n.keywords.filter((k) => typeof k === 'string' && k.trim() !== '') : [],
        sanitizeSystem: n.sanitizeSystem ?? null,
        sanitizePrompt: n.sanitizePrompt ?? null,
    };
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** プロンプトにアダルト系キーワードが含まれるか（単語境界・大小無視。keywords は config で編集可能）。 */
export function detectNsfwRisk(text, keywords = nsfwConfig().keywords) {
    if (typeof text !== 'string' || text.trim() === '' || keywords.length === 0) return { risky: false, matches: [] };
    const matches = keywords.filter((k) => new RegExp(`\\b${escapeRegExp(k)}\\b`, 'i').test(text));
    return { risky: matches.length > 0, matches };
}

/** 性的・扇情的表現を穏当化した英語プロンプトへ書き換える（imagePrompt タスクの LLM を利用）。 */
async function sanitizePromptEn(promptEn, onLog) {
    const nsfw = nsfwConfig();
    if (!nsfw.sanitizePrompt) throw new Error('config/llm.json の image.nsfw.sanitizePrompt が未設定です（プロンプト自動修正を使うには設定が必要）');
    const r = await callLlm('imagePrompt', {
        ...(nsfw.sanitizeSystem ? { system: nsfw.sanitizeSystem } : {}),
        prompt: fillTemplate(nsfw.sanitizePrompt, { prompt: promptEn }),
        onLog,
    });
    const text = String(r.text ?? '').trim().replace(/^```[^\n]*\n?/, '').replace(/\n?```\s*$/, '').trim();
    if (text === '') throw new Error('プロンプト自動修正の応答が空でした（再実行するか、プロンプトを手動で調整してください）');
    return text;
}

/**
 * キャスト画像を自動生成する（一括実行時は設定上限まで並列。1 体の失敗では止めず末尾で集計）。
 * @param {object} opts { castIds?: 対象 castId 配列（省略時は promptEn を持つ全キャスト）, provider?: 'workers-ai'|'openrouter', model?: モデル ID 上書き,
 *                        nsfwPolicy?: 'grok'（該当キャストのみ規制の緩い推奨モデルへ切替）|'sanitize'（プロンプトを穏当化して選択モデルのまま）|'asis'（警告のみ）,
 *                        batch?: 一括実行の並列化を有効にする, onLog }
 */
export async function runImageGenerate(projectId, { castIds = null, provider = null, model = null, nsfwPolicy = null, batch = false, onLog = () => {}, signal = null } = {}) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const idx = readIndexOrThrow(p);
    const targets = idx.prompts.filter(
        (e) => (!castIds || castIds.includes(e.castId)) && typeof e.promptEn === 'string' && e.promptEn.trim() !== '',
    );
    if (targets.length === 0) {
        throw new Error('対象キャストがありません（castIds の指定、または index.json の promptEn を確認してください）。');
    }
    const spec = resolveImageCallSpec(llmConfig().image ?? {}, { provider, model });
    const effProvider = spec.provider;
    const nsfw = nsfwConfig();
    onLog(`キャスト画像を ${targets.length}体分自動生成します（${spec.provider} / ${spec.model}${nsfwPolicy ? `・アダルト表現時の方針: ${nsfwPolicy}` : ''}）`);

    const generated = [];
    const failures = [];
    const castLogs = {};
    const targetOrder = new Map(targets.map((entry, index) => [entry.castId, index]));
    const concurrency = batchCharacterConcurrency('imageGeneration', batch);
    let stopScheduling = false;
    await mapWithConcurrency(targets, async (entry) => {
        const castLines = [];
        castLogs[entry.castId] = castLines;
        const castLog = (line) => {
            castLines.push(String(line));
            onLog(line);
        };
        try {
            castLog(`  ── ${entry.nameJa}（${entry.castId}）を生成中…`);
            // アダルト系表現の検知と回避（計画書§16.10。切替・修正は該当キャストのみに適用）
            let prompt = entry.promptEn;
            let entryModel = model;
            const nsfwPatch = { imageNsfwPolicy: null, promptEnSanitized: null };
            const risk = detectNsfwRisk(prompt, nsfw.keywords);
            if (risk.risky) {
                const hit = risk.matches.slice(0, 6).join(', ');
                if (nsfwPolicy === 'grok' && nsfw.recommendedModel && spec.model !== nsfw.recommendedModel) {
                    entryModel = nsfw.recommendedModel;
                    nsfwPatch.imageNsfwPolicy = 'grok';
                    castLog(`    ⚠ アダルト系表現を検知（${hit}）→ 規制の緩い ${nsfw.recommendedModel} へ切り替えて生成します`);
                } else if (nsfwPolicy === 'sanitize') {
                    castLog(`    ⚠ アダルト系表現を検知（${hit}）→ プロンプトを自動修正してから生成します`);
                    prompt = await sanitizePromptEn(prompt, castLog);
                    nsfwPatch.imageNsfwPolicy = 'sanitize';
                    nsfwPatch.promptEnSanitized = prompt;
                    castLog(`    修正後プロンプト: ${prompt.slice(0, 140)}${prompt.length > 140 ? '…' : ''}`);
                } else {
                    castLog(`    ⚠ アダルト系表現を検知（${hit}）。モデルの規制で生成が拒否される可能性があります（拒否されたら「${nsfw.recommendedModel ?? 'grok'} へ切替」か「プロンプト自動修正」を選んで再実行）`);
                }
            }
            const r = await callImage({ prompt, provider, model: entryModel, onLog: castLog });
            let bytes = Buffer.from(r.imageBase64, 'base64');
            let contentType = r.contentType;
            // 全モデル PNG 透明背景へ統一（Meshy image-to-3D の精度向上・計画書§16.10。失敗しても生成は無駄にしない＝原本保存）
            const post = llmConfig().image?.postProcess ?? {};
            if (post.transparentPng !== false) {
                try {
                    const tolerance = Number(post.backgroundTolerancePerModel?.[r.model] ?? post.backgroundTolerance) || 32;
                    // ブラウザ版シム（Canvas）は async のため await（Node 版の同期戻り値も await で両対応）
                    const t = await toTransparentPng(bytes, contentType, { tolerance });
                    bytes = t.buffer;
                    contentType = t.contentType;
                    if (t.mode === 'native') castLog('    🪄 透過PNG: モデル出力が元から透明背景（そのまま保存）');
                    else if (t.mode === 'removed') castLog(`    🪄 透過PNG: 白背景を透過化（画素の ${Math.round((t.removedRatio ?? 0) * 100)}% を除去）`);
                    else castLog(`    ⚠ 透過PNG: 非対応形式（${contentType}）のため原本のまま保存します`);
                } catch (e) {
                    castLog(`    ⚠ 透過PNG化に失敗（${String(e.message).slice(0, 120)}）。原本のまま保存します`);
                }
            }
            const ext = IMAGE_EXT_BY_TYPE[contentType] ?? 'png';
            const fileName = writeCastImage(p, entry.castId, bytes, ext);
            updateIndexEntry(p, idx, entry.castId, {
                imageFile: fileName,
                imageGeneratedAt: new Date().toISOString(),
                imageModel: r.model,
                imageProvider: r.provider ?? effProvider,
                imageSource: 'auto',
                ...nsfwPatch,
            });
            generated.push({
                castId: entry.castId,
                file: fileName,
                bytes: bytes.length,
                elapsedMs: r.elapsedMs,
                ...(Number.isFinite(r.usageUsd) ? { costUsd: r.usageUsd } : {}),
            });
            castLog(`    ✔ ${fileName}（${Math.round(bytes.length / 1024)}KB・${Math.round((r.elapsedMs ?? 0) / 1000)}秒）`);
        } catch (e) {
            failures.push({ castId: entry.castId, code: e.code ?? 'error', message: e.message });
            castLog(`    ✖ ${entry.castId}: ${e.message}`);
            if (['token_missing', 'openrouter_auth', 'openrouter_credits'].includes(e.code)) {
                // キー未設定・無効・残高切れは残りキャストも全て失敗するため中断する
                castLog('  API キーまたは残高の問題のため中断します（解消後、castIds 指定で残りだけ再実行できます）');
                stopScheduling = true;
            }
        }
    }, { concurrency, signal, shouldStart: () => !stopScheduling });
    generated.sort((a, b) => targetOrder.get(a.castId) - targetOrder.get(b.castId));
    failures.sort((a, b) => targetOrder.get(a.castId) - targetOrder.get(b.castId));
    recordStep(projectId, 'castImages', { ok: failures.length === 0, generated: generated.length, failed: failures.length });
    auditLog('story-cast-images', { projectId, generated: generated.length, failed: failures.length });
    onLog(`✔ 画像の自動生成が完了: 成功 ${generated.length} / 失敗 ${failures.length}`);
    return { ok: failures.length === 0, generated, failures, castLogs, index: readJsonIfExists(p.imagePromptsIndex) };
}

/**
 * 外部ツールで生成した画像への差し替え（「画像変更」）。base64 を検証して保存し index を更新する。
 * @param {object} input { dataBase64: base64（data URL 可）, fileName?: 元ファイル名（表示用） }
 */
export function saveUploadedImage(projectId, castId, { dataBase64, fileName = null } = {}) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const idx = readIndexOrThrow(p);
    const entry = idx.prompts.find((e) => e.castId === castId);
    if (!entry) throw new Error(`キャストが見つかりません: ${castId}（index.json の castId を確認してください）`);
    if (typeof dataBase64 !== 'string' || dataBase64.trim() === '') throw new Error('画像データ（base64）が空です');
    const b64 = dataBase64.replace(/^data:[^;]+;base64,/, '');
    const bytes = Buffer.from(b64, 'base64');
    if (bytes.length === 0) throw new Error('画像データを base64 として解釈できません');
    if (bytes.length > MAX_UPLOAD_BYTES) {
        throw new Error(`画像が大きすぎます（${Math.round(bytes.length / 1024 / 1024)}MB。上限 ${MAX_UPLOAD_BYTES / 1024 / 1024}MB）`);
    }
    const type = sniffImageType(bytes);
    if (!type) throw new Error('対応していない画像形式です（png / jpeg / webp のみ）');
    const file = writeCastImage(p, castId, bytes, IMAGE_EXT_BY_TYPE[type]);
    updateIndexEntry(p, idx, castId, {
        imageFile: file,
        imageGeneratedAt: new Date().toISOString(),
        imageModel: null,
        imageSource: 'upload',
        ...(fileName ? { imageOriginalName: String(fileName).slice(0, 120) } : {}),
    });
    auditLog('story-cast-image-upload', { projectId, castId, bytes: bytes.length });
    return { ok: true, castId, file, bytes: bytes.length };
}
